import { useState, useCallback } from 'react';
import { hexShiftHue } from '../utils/colorUtils';
import colorCombinations from '../data/colorCombinations.json';

export type HarmonyMode = 'random' | 'complementary' | 'triadic' | 'analogous';

export interface PaletteGenerationLimits {
  generationCountMax: number;
  combinationCountMax: number;
}

export interface GeneratedCombo {
  name?: string;
  colors: string[];
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function getColorDistance(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  if (!rgb1 || !rgb2) return Infinity;
  const rDiff = rgb1.r - rgb2.r;
  const gDiff = rgb1.g - rgb2.g;
  const bDiff = rgb1.b - rgb2.b;
  return Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff);
}

function buildHarmonyPalette(
  seedHex: string,
  count: number,
  mode: HarmonyMode
): string[] {
  const out: string[] = [seedHex];
  if (count <= 1) return out.slice(0, count);
  if (mode === 'complementary') {
    out.push(hexShiftHue(seedHex, 180));
    for (let i = 2; i < count; i++)
      out.push(hexShiftHue(seedHex, (i * 360) / count));
  } else if (mode === 'triadic') {
    out.push(hexShiftHue(seedHex, 120));
    out.push(hexShiftHue(seedHex, 240));
    for (let i = 3; i < count; i++)
      out.push(hexShiftHue(seedHex, (i * 360) / count));
  } else if (mode === 'analogous') {
    for (let i = 1; i <= (count - 1) / 2; i++)
      out.unshift(hexShiftHue(seedHex, -30 * i));
    for (let i = 1; i <= Math.floor((count - 1) / 2); i++)
      out.push(hexShiftHue(seedHex, 30 * i));
  }
  return out.slice(0, count);
}

export function usePaletteGeneration() {
  const [generationCount, setGenerationCount] = useState<string>('5');
  const [combinationCount, setCombinationCount] = useState<string>('1');
  const [harmonyMode, setHarmonyMode] = useState<HarmonyMode>('random');
  const [isGenerating, setIsGenerating] = useState(false);

  const generateFromSelection = useCallback(
    (
      selectedColors: string[],
      limits: PaletteGenerationLimits,
      canUseHarmony: boolean
    ): GeneratedCombo[] => {
      const count = parseInt(generationCount, 10);
      const comboCount = Math.min(
        parseInt(combinationCount, 10),
        limits.combinationCountMax
      );
      const useHarmony = canUseHarmony && harmonyMode !== 'random';
      const combinations: GeneratedCombo[] = [];

      for (let comboIndex = 0; comboIndex < comboCount; comboIndex++) {
        if (useHarmony) {
          const seed = selectedColors[comboIndex % selectedColors.length];
          combinations.push({
            colors: buildHarmonyPalette(seed, count, harmonyMode),
          });
          continue;
        }
        if (count === selectedColors.length) {
          const shuffled = [...selectedColors].sort(() => Math.random() - 0.5);
          combinations.push({ colors: shuffled });
          continue;
        }

        const selected = [...selectedColors].sort(() => Math.random() - 0.5);
        const palette: string[] = [];
        const randomIndex = Math.floor(Math.random() * selected.length);
        palette.push(selected.splice(randomIndex, 1)[0]);

        while (palette.length < count && selected.length > 0) {
          const colorDistances: { color: string; minDistance: number }[] = [];
          for (let i = 0; i < selected.length; i++) {
            let minDistance = Infinity;
            for (const paletteColor of palette) {
              const distance = getColorDistance(selected[i], paletteColor);
              minDistance = Math.min(minDistance, distance);
            }
            colorDistances.push({ color: selected[i], minDistance });
          }
          colorDistances.sort((a, b) => b.minDistance - a.minDistance);
          const candidateCount = Math.max(
            2,
            Math.ceil(colorDistances.length * 0.5)
          );
          const candidates = colorDistances.slice(0, candidateCount);
          const randomCandidate =
            candidates[Math.floor(Math.random() * candidates.length)];
          const colorIndex = selected.indexOf(randomCandidate.color);
          if (colorIndex !== -1) {
            palette.push(selected.splice(colorIndex, 1)[0]);
          } else {
            palette.push(
              selected.splice(Math.floor(Math.random() * selected.length), 1)[0]
            );
          }
        }
        combinations.push({ colors: palette });
      }

      return combinations;
    },
    [generationCount, combinationCount, harmonyMode]
  );

  const generateRandom = useCallback(
    (limits: PaletteGenerationLimits): GeneratedCombo[] => {
      const count = parseInt(generationCount, 10);
      const comboCount = Math.min(
        parseInt(combinationCount, 10),
        limits.combinationCountMax
      );
      const allCombinations = colorCombinations.combinations;
      const shuffled = [...allCombinations].sort(() => Math.random() - 0.5);
      const selectedCombos = shuffled.slice(
        0,
        Math.min(comboCount, limits.combinationCountMax)
      );
      return selectedCombos.map((combo) => ({
        name: combo.name,
        colors: combo.colors.slice(0, Math.min(count, combo.colors.length)),
      }));
    },
    [generationCount, combinationCount]
  );

  return {
    generationCount,
    setGenerationCount,
    combinationCount,
    setCombinationCount,
    harmonyMode,
    setHarmonyMode,
    isGenerating,
    setIsGenerating,
    generateFromSelection,
    generateRandom,
  };
}
